import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { operationsApi } from '@/api/operations';
import { toast } from '@/stores/toastStore';
import { CATEGORIE_FOURNISSEUR_LABELS, type CategorieFournisseur } from '@/types';
import { Modal, ModalHeader } from './_ui';

/**
 * SIMG-ACHATS-LEADBETON-2026-002 — Modale création rapide fournisseur.
 * Utilisée depuis NouveauBcaPage (sélecteur + Nouveau) et FournisseursPage
 * (bouton en tête de page). Auto-sélection via `onCreated`.
 */
export function NouveauFournisseurDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [raisonSociale, setRs] = useState('');
  const [categorie, setCat]    = useState<CategorieFournisseur>('cimenterie');
  const [tel, setTel]          = useState('');
  const [email, setEmail]      = useState('');
  const [delaiPaiement, setD]  = useState(30);

  const creerMut = useMutation({
    mutationFn: (body: Parameters<typeof operationsApi.beton.createFournisseur>[0]) =>
      operationsApi.beton.createFournisseur(body),
    onSuccess: (f) => {
      qc.invalidateQueries({ queryKey: ['achats', 'fournisseurs'] });
      toast.success('Fournisseur créé', f.raisonSociale);
      onCreated(f.id);
    },
  });

  const canSubmit = raisonSociale.trim().length >= 2;
  const submit = () => {
    if (!canSubmit) return;
    creerMut.mutate({
      raisonSociale:      raisonSociale.trim(),
      categorie,
      tel:                tel.trim() || undefined,
      email:              email.trim() || undefined,
      delaiPaiementJours: delaiPaiement,
      actif:              true,
    });
  };

  return (
    <Modal onClose={onClose}>
      <div style={{ maxWidth: 480, width: '100%', padding: 20 }}>
        <ModalHeader titre="Nouveau fournisseur" onClose={onClose} />
        <p style={{ fontSize: 13, color: 'var(--tx-3, #6B7280)', marginTop: 0 }}>
          Champs minimum. Complète RCCM/adresse/IBAN plus tard depuis le back-office.
        </p>

        <div style={{ display: 'grid', gap: 10 }}>
          <label>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 4 }}>Raison sociale *</div>
            <input
              autoFocus type="text" value={raisonSociale} onChange={(e) => setRs(e.target.value)}
              placeholder="ex. Cimenterie CIMAF SA"
              style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--bd-2, #D1D5DB)', borderRadius: 8, fontSize: 14 }}
            />
          </label>
          <label>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 4 }}>Catégorie *</div>
            <select
              value={categorie} onChange={(e) => setCat(e.target.value as CategorieFournisseur)}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--bd-2)', borderRadius: 8, fontSize: 14, background: '#fff' }}
            >
              {(Object.keys(CATEGORIE_FOURNISSEUR_LABELS) as CategorieFournisseur[]).map((c) => (
                <option key={c} value={c}>{CATEGORIE_FOURNISSEUR_LABELS[c]}</option>
              ))}
            </select>
          </label>
          <label>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 4 }}>Téléphone</div>
            <input
              type="tel" value={tel} onChange={(e) => setTel(e.target.value)}
              placeholder="+225 07 07 07 07 07"
              style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--bd-2)', borderRadius: 8, fontSize: 14 }}
            />
          </label>
          <label>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 4 }}>Email</div>
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="contact@fournisseur.ci"
              style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--bd-2)', borderRadius: 8, fontSize: 14 }}
            />
          </label>
          <label>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 4 }}>Délai paiement (jours)</div>
            <input
              type="number" min={0} value={delaiPaiement} onChange={(e) => setD(Number(e.target.value))}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--bd-2)', borderRadius: 8, fontSize: 14 }}
            />
          </label>
        </div>

        <div style={{ marginTop: 20, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--bd-1)', background: '#fff', cursor: 'pointer' }}
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit || creerMut.isPending}
            style={{
              padding: '8px 14px', borderRadius: 8, border: 'none',
              background: canSubmit ? '#0F766E' : '#D1D5DB', color: '#fff', fontWeight: 700,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {creerMut.isPending ? 'Création…' : 'Créer et sélectionner'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
